import React, { useState } from 'react';
import { Row, Button, Dropdown, Menu, Checkbox } from 'antd';
import { PlusOutlined, DownOutlined } from '@ant-design/icons';
import { FormItemWrap } from 'react-structure-admin';
import ResCol from 'components/forms/ResCol';
import SelectExpressionOperator from 'components/SelectExpressionOperator';
import { InputCurrency } from 'components';
import { customerTypeDisplay, locationTypesDisplay } from 'constants/types';
import SectionHeader from 'components/SectionHeader';

const supportedLocationTypes = [
  'companyMunicipality',
  'outsideCompanyMunicipality',
  'foreign'
];

const supportedCustomerTypes = ['naturalPerson', 'legalPerson', 'foreign'];

const TaxationGroupServiceInvoiceConditionList = ({
  company,
  form,
  onLocationTypeChange
}) => {
  const locationTypes = form.getFieldValue(['conditions', 'locationTypes']);
  const hasLocationTypes = Array.isArray(locationTypes)
    ? locationTypes.length > 0
    : locationTypes != null;
  const hasOperator = form.getFieldValue(['conditions', 'operator']) != null;
  const customerTypes = form.getFieldValue(['conditions', 'customerTypes']);
  const hasCustomerTypes = Array.isArray(customerTypes)
    ? customerTypes.length > 0
    : customerTypes != null;

  const [showLocationTypeCondition, setShowLocationTypeCondition] = useState(
    hasLocationTypes
  );
  const [showOperatorCondition, setShowOperatorCondition] = useState(
    hasOperator
  );
  const [showCustomerTypesCondition, setShowCustomerTypesCondition] = useState(
    hasCustomerTypes
  );

  const [operator, setOperator] = useState(
    form.getFieldValue(['conditions', 'operator'])
  );

  const handleAddCondition = (type) => {
    if (type === 'location') {
      setShowLocationTypeCondition(true);
    }

    if (type === 'customer') {
      setShowCustomerTypesCondition(true);
    }

    if (type === 'operator') {
      setShowOperatorCondition(true);
    }
  };

  const handleRemoveLocationCondition = () => {
    setShowLocationTypeCondition(false);

    const currentConditions = form.getFieldValue('conditions') ?? {};

    form.setFieldsValue({
      conditions: {
        ...currentConditions,
        locationTypes: null
      }
    });

    if (onLocationTypeChange) {
      onLocationTypeChange(null);
    }
  };

  const handleRemoveCustomerCondition = () => {
    setShowCustomerTypesCondition(false);

    const currentConditions = form.getFieldValue('conditions') ?? {};

    form.setFieldsValue({
      conditions: {
        ...currentConditions,
        customerTypes: null
      }
    });
  };

  const handleRemoveOperatorCondition = () => {
    setShowOperatorCondition(false);
    setOperator(null);

    const currentConditions = form.getFieldValue('conditions') ?? {};

    form.setFieldsValue({
      conditions: {
        ...currentConditions,
        operator: null,
        amount: null
      }
    });
  };

  const menu = (
    <Menu onClick={({ key }) => handleAddCondition(key)}>
      {!showLocationTypeCondition && (
        <Menu.Item key="location">Localidade do cliente</Menu.Item>
      )}
      {!showCustomerTypesCondition && (
        <Menu.Item key="customer">Tipo do cliente</Menu.Item>
      )}
      {!showOperatorCondition && (
        <Menu.Item key="operator">Valor da NFS-e</Menu.Item>
      )}
    </Menu>
  );

  const locationOptions = supportedLocationTypes.map((type) => ({
    label: locationTypesDisplay[type],
    value: type
  }));

  const customerOptions = supportedCustomerTypes.map((type) => ({
    label: customerTypeDisplay[type],
    value: type
  }));

  const handleChangeLocationType = (value) => {
    form.setFieldsValue({
      conditions: { ...form.getFieldValue('conditions'), locationTypes: value }
    });

    if (onLocationTypeChange) {
      onLocationTypeChange(value);
    }
  };

  const handleChangeCustomerType = (value) => {
    form.setFieldsValue({
      conditions: { ...form.getFieldValue('conditions'), customerTypes: value }
    });
  };

  const handleChangeOperator = (value) => {
    setOperator(value);

    if (!value) {
      form.setFieldsValue({
        conditions: { ...form.getFieldValue('conditions'), amount: null }
      });
    }
  };

  const showAddButton =
    !showLocationTypeCondition ||
    !showOperatorCondition ||
    !showCustomerTypesCondition;

  return (
    <>
      {showLocationTypeCondition && (
        <>
          <SectionHeader
            label="Localidade do cliente"
            onRemove={handleRemoveLocationCondition}
          />
          <Row>
            <ResCol span={24}>
              <FormItemWrap name={['conditions', 'locationTypes']}>
                <Checkbox.Group
                  options={locationOptions}
                  onChange={handleChangeLocationType}
                />
              </FormItemWrap>
            </ResCol>
          </Row>
        </>
      )}

      {showCustomerTypesCondition && (
        <>
          <SectionHeader
            label="Tipo do cliente"
            onRemove={handleRemoveCustomerCondition}
          />
          <Row>
            <ResCol span={24}>
              <FormItemWrap name={['conditions', 'customerTypes']}>
                <Checkbox.Group
                  options={customerOptions}
                  onChange={handleChangeCustomerType}
                />
              </FormItemWrap>
            </ResCol>
          </Row>
        </>
      )}

      {showOperatorCondition && (
        <>
          <SectionHeader
            label="Valor NFS-e"
            onRemove={handleRemoveOperatorCondition}
          />
          <Row>
            <ResCol span={12}>
              <FormItemWrap name={['conditions', 'operator']} label="Operador">
                <SelectExpressionOperator onChange={handleChangeOperator} />
              </FormItemWrap>
            </ResCol>

            <ResCol span={12}>
              <FormItemWrap name={['conditions', 'amount']} label="Valor">
                <InputCurrency
                  decimalsLimit={company?.decimalPrecision ?? 2}
                  decimalScale={company?.decimalPrecision ?? 2}
                  disabled={!operator}
                />
              </FormItemWrap>
            </ResCol>
          </Row>
        </>
      )}

      {showAddButton && (
        <div style={{ textAlign: 'right', marginTop: '8px' }}>
          <Dropdown overlay={menu} trigger={['click']}>
            <Button icon={<PlusOutlined />}>
              Adicionar condição <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      )}
    </>
  );
};

export default TaxationGroupServiceInvoiceConditionList;
